/**
 * ApkInstaller Plugin - Lazy Loading Version
 *
 * Uses Capacitor.isPluginAvailable() to check if the native plugin exists
 * before registering, preventing "not implemented on android" errors when
 * the native APK doesn't include the plugin.
 *
 * Methods:
 * - isDeviceOwner(): Check if app is running as Device Owner
 * - installApk({ apkUrl }): Download and install APK (silent if Device Owner)
 */

let _pluginInstance = null;
let _initializationPromise = null;

/**
 * Web fallback implementation
 */
class ApkInstallerWeb {
  async isDeviceOwner() {
    return { isDeviceOwner: false };
  }

  async installApk(options = {}) {
    console.log('[ApkInstallerWeb] APK installation not available on web');
    return { success: false, message: 'APK installation not available on web' };
  }
}

/**
 * Initialize the plugin (lazy, thread-safe)
 */
const getPlugin = async () => {
  if (_pluginInstance) {
    return _pluginInstance;
  }

  if (_initializationPromise) {
    return _initializationPromise;
  }

  _initializationPromise = (async () => {
    try {
      const { Capacitor, registerPlugin } = await import('@capacitor/core');

      // Only use native plugin if it actually exists in the native APK
      if (Capacitor.isPluginAvailable('ApkInstaller')) {
        _pluginInstance = registerPlugin('ApkInstaller');
        console.log('[ApkInstaller] Native plugin available');
      } else {
        console.log('[ApkInstaller] Native plugin not available, using web fallback');
        _pluginInstance = new ApkInstallerWeb();
      }
    } catch (err) {
      console.warn('[ApkInstaller] Init failed, using web fallback:', err.message);
      _pluginInstance = new ApkInstallerWeb();
    }

    return _pluginInstance;
  })();

  return _initializationPromise;
};

/**
 * Proxy object that lazy-loads the actual plugin on first use
 */
const ApkInstaller = {
  async isDeviceOwner() {
    try {
      const plugin = await getPlugin();
      return await plugin.isDeviceOwner();
    } catch (err) {
      console.error('[ApkInstaller] isDeviceOwner failed:', err);
      return { isDeviceOwner: false, error: err.message };
    }
  },

  async installApk(options = {}) {
    try {
      const plugin = await getPlugin();
      return await plugin.installApk(options);
    } catch (err) {
      console.error('[ApkInstaller] installApk failed:', err);
      return { success: false, error: err.message };
    }
  },
};

export default ApkInstaller;
